import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatRM, formatDate, STATUSES, jobPic, statusColor } from "@/lib/api";
import { StatusBadge, AgentBadge } from "@/components/StatusBadge";
import { Countdown } from "@/components/Countdown";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Flame, AlertCircle, Printer, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { waJobMessage, waAgentJobMessage } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const status = params.get("status") || "all";
  const agent = params.get("agent") || "all";
  const due = params.get("due");
  const priority = params.get("priority");
  const [agents, setAgents] = useState([]);
  const nav = useNavigate();
  const { hasPerm, user } = useAuth();
  const canSales = hasPerm("sales");
  const isAdmin = user?.role === "admin";

  const delJob = async (j, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete cancelled job ${j.job_number}? This permanently removes it and its files.`)) return;
    try { await api.delete(`/jobs/${j.id}`); toast.success("Job deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed to delete job"); }
  };

  useEffect(() => { api.get("/users").then((r) => setAgents(r.data.filter((u) => u.role === "agent"))); }, []);

  const setParam = (k, v) => { const np = new URLSearchParams(params); if (v === "all") np.delete(k); else np.set(k, v); setParams(np); };

  const load = useCallback(() => {
    const p = {};
    if (status !== "all") p.status = status;
    if (due) p.due = due;
    if (priority) p.priority = priority;
    if (agent === "agent" || agent === "non_agent") p.source = agent;
    else if (agent !== "all") p.agent_id = agent;
    if (q) p.q = q;
    api.get("/jobs", { params: p }).then((r) => { setJobs(r.data); setSelected([]); });
  }, [status, due, priority, agent, q]);

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSel = jobs.length > 0 && selected.length === jobs.length;
  const printLabels = (ids) => nav(`/labels?ids=${ids.join(",")}`);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load, 20000);
  const quickFilter = due === "today" ? "Due Today" : due === "overdue" ? "Overdue" : priority === "urgent" ? "Urgent" : null;

  return (
    <div className="space-y-4" data-testid="jobs-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">All Jobs</h1>
          <p className="text-sm text-muted-foreground">{jobs.length} job(s) found{selected.length > 0 && ` • ${selected.length} selected`}
            {quickFilter && <button onClick={() => { const np = new URLSearchParams(params); np.delete("due"); np.delete("priority"); setParams(np); }} className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 text-xs font-semibold" data-testid="jobs-quick-filter">{quickFilter} ×</button>}
          </p>
        </div>
        <div className="flex gap-2">
          {selected.length > 0 && <Button size="sm" variant="outline" onClick={() => printLabels(selected)} data-testid="print-selected-labels"><Printer className="w-4 h-4 mr-1" /> Print {selected.length} Label(s)</Button>}
          <Button size="sm" variant="outline" disabled={!jobs.length} onClick={() => printLabels(jobs.map((j) => j.id))} data-testid="print-all-labels"><Printer className="w-4 h-4 mr-1" /> Print All Labels</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search RZF-ID, customer, product..."
            className="pl-9"
            data-testid="jobs-search"
          />
        </div>
        <Select value={status} onValueChange={(v) => { const np = new URLSearchParams(params); if (v === "all") np.delete("status"); else np.set("status", v); setParams(np); }}>
          <SelectTrigger className="w-56" data-testid="jobs-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={agent} onValueChange={(v) => setParam("agent", v)}>
          <SelectTrigger className="w-52" data-testid="jobs-agent-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="agent">Agent orders only</SelectItem>
            <SelectItem value="non_agent">Non-agent</SelectItem>
            {agents.map((a) => <SelectItem key={a.id} value={a.id}>Agent: {a.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="jobs-refresh">Refresh</Button>
      </div>

      <div className="border rounded-xl bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-3 w-8"><Checkbox checked={allSel} onCheckedChange={(v) => setSelected(v ? jobs.map((j) => j.id) : [])} data-testid="select-all-jobs" /></th>
                <th className="text-left px-4 py-3">Job No.</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">PIC</th>
                <th className="text-left px-4 py-3">Deadline</th>
                <th className="px-2 py-3">WA</th>
                {canSales && <th className="text-right px-4 py-3">Total</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {jobs.map((j) => (
                <tr key={j.id} onClick={() => nav(`/jobs/${j.id}`)} style={{ borderLeft: `4px solid ${statusColor(j.status)}` }} className={`hover:bg-muted/50 cursor-pointer ${selected.includes(j.id) ? "bg-cyan-50 dark:bg-cyan-950/20" : ""}`} data-testid={`job-row-${j.job_number}`}>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(j.id)} onCheckedChange={() => toggleSel(j.id)} data-testid={`select-job-${j.job_number}`} /></td>
                  <td className="px-4 py-3 font-mono font-semibold flex items-center gap-2">
                    {j.priority === "urgent" && <Flame className="w-3.5 h-3.5 text-orange-500" />}
                    {j.job_number}
                    <AgentBadge job={j} />
                  </td>
                  <td className="px-4 py-3">{j.customer_name}{j.agent_name && <div className="text-xs text-amber-600">via {j.agent_name}</div>}</td>
                  <td className="px-4 py-3 text-muted-foreground">{j.product}</td>
                  <td className="px-4 py-3"><StatusBadge status={j.status} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{jobPic(j)}</td>
                  <td className="px-4 py-3 font-mono text-xs"><div className="flex items-center gap-2">{formatDate(j.deadline)}<Countdown job={j} /></div></td>
                  <td className="px-2 py-3"><div className="flex gap-1 items-center">
                    <WhatsAppButton size="icon" phone={j.customer_phone} message={waJobMessage(j)} testid={`wa-customer-${j.job_number}`} />
                    {j.agent_phone && <WhatsAppButton size="icon" phone={j.agent_phone} className="bg-amber-500 hover:bg-amber-600" message={waAgentJobMessage(j)} testid={`wa-agent-${j.job_number}`} />}
                    {isAdmin && j.status === "Cancelled" && <button onClick={(e) => delJob(j, e)} className="p-1.5 rounded text-rose-600 hover:bg-rose-50" title="Delete cancelled job" data-testid={`delete-job-${j.job_number}`}><Trash2 className="w-4 h-4" /></button>}
                  </div></td>
                  {canSales && <td className="px-4 py-3 text-right font-mono">{formatRM(j.total_price)}</td>}
                </tr>
              ))}
              {jobs.length === 0 && (
                <tr><td colSpan="9" className="px-4 py-10 text-center text-muted-foreground">
                  <AlertCircle className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  No jobs found.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
